import { Presence } from '@radix-ui/react-presence';
import { type CSSProperties, memo } from 'react';

import { Portal } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentPanelRoot } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelRoot';
import { WorkspaceAgentPanelSlotClip } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelSlotClip';
import { useWorkspaceAgentPanelSpacerEnter } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelSpacerEnter';
import {
  useWorkspaceAgentPanelViewport,
  type WorkspaceAgentPanelSurface,
} from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelViewport';
import { type PrivateWorkspaceAgentPanelProps } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanel';
import { type WorkspaceAgentPanelSlotWidthAnimationStyle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelDockedLayout';
import { WORKSPACE_AGENT_PANEL_SLOT_WIDTH_CSS_VARIABLE } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { useSyncElementWidthCssVariable } from '~/components/v2/hooks/useSyncElementWidthCssVariable';
import { WORKSPACE_UI2026_SURFACE_GAP } from '~/constants/panelWidths';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type PrivateWorkspaceAgentPanelAffordanceProps = Pick<PrivateWorkspaceAgentPanelProps, 'canvasOpen'>;

export type PortalledWorkspaceAgentPanelProps = {
  isSlotOpen: boolean;
  shouldAnimateSlot: boolean;
  slotClipStyle: CSSProperties | null;
  slotWidthAnimationStyle: WorkspaceAgentPanelSlotWidthAnimationStyle;
  style: CSSProperties;
  surface: WorkspaceAgentPanelSurface;
};

const PortalledWorkspaceAgentPanel = memo(
  ({
    isSlotOpen,
    shouldAnimateSlot,
    slotClipStyle,
    slotWidthAnimationStyle,
    style,
    surface,
  }: PortalledWorkspaceAgentPanelProps) => {
    return (
      <WorkspaceAgentPanelSlotClip
        isSlotOpen={isSlotOpen}
        shouldAnimateSlot={shouldAnimateSlot}
        slotClipStyle={slotClipStyle}
        slotWidthAnimationStyle={slotWidthAnimationStyle}
      >
        <PrivateWorkspaceAgentPanelRoot isSlotClipped={slotClipStyle !== null} style={style} surface={surface} />
      </WorkspaceAgentPanelSlotClip>
    );
  },
);

PortalledWorkspaceAgentPanel.displayName = 'PortalledWorkspaceAgentPanel';

/**
 * Spacer + permanent body portal for the expanded panel. Minimized workspace chrome is
 * `PrivateWorkspaceAgentSpace` (layout-mounted slide-up selection bar).
 *
 * Chat and tasks share one WorkspaceAgentPanel card: the in-flow spacer grows when the slot opens,
 * and switching chat/tasks only swaps the card's inner content. On ui2026 the spacer is a sibling
 * of the whole chrome card (header included); the portalled card stays at stored width inside a
 * right-anchored overflow clip so open/close is CSS `animate-width-in` / `animate-width-out`, the
 * same drawer as the ad sidebar, sidenav, and custom-fields panel. Pre-ui2026 it still sits in the
 * content row and clears the 40px status-bar strip.
 */
export const PrivateWorkspaceAgentPanelAffordance = memo(
  ({ canvasOpen }: PrivateWorkspaceAgentPanelAffordanceProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const {
      isExpanded,
      isSlotOpen,
      panelStyle,
      showSpacer,
      slotClipStyle,
      slotWidthAnimationStyle,
      spacerRef,
      surface,
    } = useWorkspaceAgentPanelViewport({
      canvasOpen,
      ui2026,
    });
    const { hasHydratedStore, shouldAnimateSlotEnter, shouldAnimateSpacerEnter } = useWorkspaceAgentPanelSpacerEnter({
      isExpanded,
      isSlotOpen,
      showSpacer,
    });
    const { syncElementWidthRef } = useSyncElementWidthCssVariable({
      clearedValue: '0px',
      cssVariable: WORKSPACE_AGENT_PANEL_SLOT_WIDTH_CSS_VARIABLE,
      enabled: ui2026,
      forwardRef: spacerRef,
      getCssValue: (widthPx) => {
        const slotWidth = widthPx > 0 ? widthPx + WORKSPACE_UI2026_SURFACE_GAP : 0;

        return `${slotWidth}px`;
      },
    });
    const shouldMountSpacer = hasHydratedStore && showSpacer;
    const shouldAnimateSpacer = shouldAnimateSpacerEnter || !shouldMountSpacer;
    const shouldAnimateClip = shouldAnimateSlotEnter || !isSlotOpen;

    return (
      <>
        <Presence present={shouldMountSpacer}>
          <div
            aria-hidden
            className={cn(
              'animation-duration-150 shrink-0 overflow-hidden data-[animate=true]:motion-safe:radix-state-closed:animate-width-out data-[animate=true]:motion-safe:radix-state-open:animate-width-in',
              ui2026 ? 'self-stretch' : 'mr-2 mb-10 h-[calc(100%-2.5rem)]',
            )}
            data-animate={shouldAnimateSpacer || undefined}
            data-state={shouldMountSpacer ? 'open' : 'closed'}
            ref={syncElementWidthRef}
            style={slotWidthAnimationStyle}
          >
            {/* Inner stored width so the clip reveals the card instead of squashing it — AdDetailsSidebar. */}
            <div className="h-full" style={{ width: slotWidthAnimationStyle.width }} />
          </div>
        </Presence>

        {/*
        The portal container is mounted unconditionally so it is created once and never replaced.

        It has to be a portal at all because the asset modal is itself a body-level portal appended
        after `#__next`, and `#__next` is a stacking context (`position: relative; z-index: 0` in
        globals.scss) — so no z-index applied from inside the workspace tree can lift the panel over
        the modal. It has to be permanent rather than only while the asset view is open because
        swapping a portal's container remounts its children, which would cost the composer draft,
        the thread's scroll position and the TipTap instance on every asset a user opened.

        The card inside mounts whenever `panelStyle` is set, not only while `dock !== 'minimized'`.
        On close, Presence keeps the clip mounted through `animate-width-out`. Clip Presence keys
        off the docked slot, not the spacer: a floating panel is expanded with the spacer down, and
        the spacer does not mount just because the asset modal is open — that gutter would squeeze
        the gallery underneath and collapse when the modal closes. On ui2026 the clip is
        right-anchored so CSS width-in grows left, matching an in-flow last flex child.
        Opening or closing the asset modal only swaps clip geometry (workspace spacer vs under the
        modal header) so an already-open panel does not replay enter. Dock vs float also only
        swaps clip geometry — the wrapper tree stays mounted so drag-to-undock cannot remount the
        card (composer, TipTap, thread scroll, window pointer listeners).
      */}
        <Portal>
          {panelStyle ? (
            <PortalledWorkspaceAgentPanel
              isSlotOpen={isSlotOpen}
              shouldAnimateSlot={shouldAnimateClip}
              slotClipStyle={slotClipStyle}
              slotWidthAnimationStyle={slotWidthAnimationStyle}
              style={panelStyle}
              surface={surface}
            />
          ) : null}
        </Portal>
      </>
    );
  },
);

PrivateWorkspaceAgentPanelAffordance.displayName = 'PrivateWorkspaceAgentPanelAffordance';
